import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MapPin, Phone, Truck, CheckCircle, Package, Lock, Lightning, Clock,
  ArrowRight, House, CalendarBlank, CalendarCheck, MapTrifold, ListBullets,
  SkipForward, X, ArrowClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { staffApi, formatStaffErr } from "../../lib/staffApi";
import TaskMap from "./TaskMap";

const PRIORITY_STYLE = {
  NORMAL: "bg-[#D4E0D1] text-[#1A3317]",
  HIGH: "bg-[#F9E4B7] text-[#7A5A11]",
  URGENT: "bg-[#F4C7B9] text-[#7A2E18]",
  EMERGENCY: "bg-[#F4C7B9] text-[#7A2E18]",
};

// Iter 80 — mirror backend UTC date (staff_app_routes.py:459) so
// tasks scheduled for "today" land in the Today bucket regardless of
// the phone's local timezone offset.
const todayIso = () => new Date().toISOString().slice(0, 10);

// Human-friendly upcoming date label ("Tomorrow", "Sat 8 Feb", …).
const fmtUpcoming = (d) => {
  if (!d) return "";
  try {
    const [y, m, day] = d.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, day));
    const t = new Date();
    const tUtc = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate()));
    const diffDays = Math.round((dt - tUtc) / 86400000);
    if (diffDays === 1) return "Tomorrow";
    if (diffDays > 1 && diffDays < 7) {
      return dt.toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" });
    }
    return dt.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
  } catch (_) { return d; }
};

const Pill = ({ count, tone = "muted" }) => {
  const cls = {
    warn: "bg-[#F4C7B9] text-[#7A2E18]",
    ok: "bg-[#D4E0D1] text-[#1A3317]",
    info: "bg-[#DDE7F1] text-[#1F3A57]",
    muted: "bg-[#EEEBE2] text-[#5C5A55]",
  }[tone];
  return (
    <span className={`ml-1 inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 rounded-full text-[10px] font-bold ${cls}`}>
      {count}
    </span>
  );
};

export default function StaffTasks() {
  const nav = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skipFor, setSkipFor] = useState(null);   // task being skipped
  // Iter 122 — list ↔ map view toggle. Persist across mounts so a
  // staff who prefers the map doesn't have to re-select every visit.
  const [view, setView] = useState(() =>
    localStorage.getItem("rescigo.staff_tasks_view") || "list"
  );
  const setViewPersist = (v) => {
    setView(v);
    localStorage.setItem("rescigo.staff_tasks_view", v);
  };

  const load = () => {
    setLoading(true);
    staffApi.get("/staff-app/tasks")
      .then((r) => setTasks(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const reopen = async (t) => {
    try {
      await staffApi.post(`/staff-app/tasks/${t.id}/reopen`);
      toast.success("Task reopened");
      load();
    } catch (e) { toast.error(formatStaffErr(e)); }
  };

  const iso = todayIso();
  const isOpen = (t) => t.status === "pending" || t.status === "in_progress";
  const isSkipped = (t) => t.status === "skipped";

  // Iter 80 — three time-based buckets so field staff can eyeball
  // what needs clearing today vs later.
  const backlog = tasks.filter((t) => t.is_backlog && isOpen(t));
  const todayTasks = tasks.filter((t) => !t.is_backlog && (t.date === iso) && isOpen(t));
  const upcoming = tasks.filter((t) => !t.is_backlog && t.date && t.date > iso && isOpen(t));
  const current = todayTasks.find((t) => t.is_current);
  const todayQueue = todayTasks.filter((t) => !current || t.id !== current.id);
  // Iter 185 — skipped bucket (later_service / wrong_address) — tap to reopen.
  const skipped = tasks.filter(isSkipped);
  const done = tasks.filter(
    (t) => !t.is_backlog && (t.status === "completed" || t.status === "missed"),
  );
  // Iter 193 — aggregate pending bag deliveries across all tasks assigned
  // to this staff so we can surface a top banner.
  const pendingDeliveries = useMemo(() => {
    const seen = new Set();
    return tasks
      .filter((t) => t.pending_bag_delivery)
      .filter((t) => {
        const id = t.pending_bag_delivery.id;
        if (seen.has(id)) return false;
        seen.add(id); return true;
      })
      .map((t) => ({
        ...t.pending_bag_delivery,
        customer_name: t.customer?.name || t.customer_name || "Customer",
      }));
  }, [tasks]);

  return (
    <div className="space-y-5" data-testid="staff-tasks">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">
            Today · {todayTasks.length} stop{todayTasks.length === 1 ? "" : "s"}
          </div>
          <h1 className="font-heading text-xl text-[#1A1A1A]">My route</h1>
        </div>
        {/* Iter 122 — list ↔ map view toggle */}
        <div className="inline-flex bg-white border border-[#E5E2D9] rounded-md overflow-hidden text-[11px] font-semibold"
             data-testid="staff-tasks-view-toggle">
          <button onClick={() => setViewPersist("list")}
                  data-testid="staff-tasks-view-list"
                  className={`h-9 px-3 inline-flex items-center gap-1.5 transition-colors ${
                    view === "list"
                      ? "bg-[#2D5A27] text-white"
                      : "text-[#5C5A55] hover:bg-[#F7F6F2]"
                  }`}>
            <ListBullets size={13} weight="bold"/>List
          </button>
          <button onClick={() => setViewPersist("map")}
                  data-testid="staff-tasks-view-map"
                  className={`h-9 px-3 inline-flex items-center gap-1.5 transition-colors ${
                    view === "map"
                      ? "bg-[#2D5A27] text-white"
                      : "text-[#5C5A55] hover:bg-[#F7F6F2]"
                  }`}>
            <MapTrifold size={13} weight="bold"/>Map
          </button>
        </div>
      </div>

      {/* Iter 193 — Top banner listing every pending bag delivery for
           this staff+day. Tap to jump to that delivery job. */}
      {pendingDeliveries.length > 0 && (
        <div className="bg-[#F9EDE7] border border-[#C25D38] rounded-md p-3"
              data-testid="staff-tasks-pending-deliveries-banner">
          <div className="flex items-center gap-2 mb-1.5">
            <Package size={14} weight="fill" className="text-[#7A2E18]"/>
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#7A2E18]">
              Bag delivery pending — assigned to you
            </div>
            <span className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-white text-[#7A2E18] border border-[#F4C7B9]">
              {pendingDeliveries.length}
            </span>
          </div>
          <div className="space-y-1">
            {pendingDeliveries.map(d => (
              <Link key={d.id}
                     to={`/staff-app/deliveries?open=${d.id}`}
                     data-testid={`staff-tasks-pending-delivery-${d.id}`}
                     className="flex items-center gap-2 text-[12px] bg-white rounded px-2 py-1.5 border border-[#F4C7B9] hover:border-[#C25D38]">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#1A1A1A] truncate">
                    {d.customer_name}
                    {d.challan_no && (
                      <span className="ml-2 text-[10px] font-mono text-[#8A8780]">
                        {d.challan_no}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-[#7A2E18]">
                    {d.bag_count != null ? `${d.bag_count} bag${d.bag_count !== 1 ? "s" : ""}` : "—"}
                    {d.status && ` · ${d.status.replace(/_/g, " ")}`}
                  </div>
                </div>
                <span className="text-[11px] font-semibold text-[#7A2E18]">
                  Deliver →
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {view === "map" && !loading && (
        <TaskMap tasks={tasks} onOpenTask={(t) => nav(`/staff-app/tasks/${t.id}`)}/>
      )}

      {view === "list" && loading && <div className="text-xs text-[#8A8780]">Loading…</div>}
      {view === "list" && !loading && tasks.length === 0 && (
        <div className="bg-white border border-[#E5E2D9] rounded-lg p-8 text-center" data-testid="staff-no-tasks">
          <Truck size={30} className="inline-block mb-3 opacity-40" />
          <div className="text-sm text-[#5C5A55]">No tasks assigned today</div>
          <div className="text-xs text-[#8A8780] mt-1">
            Your supervisor will assign your route shortly.
          </div>
        </div>
      )}

      {view === "list" && backlog.length > 0 && (
        <section data-testid="staff-backlog-tasks">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#7A2E18] font-semibold mb-2 inline-flex items-center gap-1.5">
            <Clock size={12} weight="fill" />
            Backlog
            <Pill count={backlog.length} tone="warn" />
            <span className="ml-1 normal-case tracking-normal text-[10px] text-[#7A2E18]/80 font-normal">
              pending from earlier days
            </span>
          </div>
          <div className="space-y-2">
            {backlog.map((t) => <Card key={t.id} t={t} backlog onSkip={setSkipFor} />)}
          </div>
        </section>
      )}

      {view === "list" && (current || todayQueue.length > 0) && (
        <section data-testid="staff-today-tasks">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#2D5A27] font-semibold mb-2 inline-flex items-center gap-1.5">
            <CalendarCheck size={12} weight="fill" />
            Today
            <Pill count={todayTasks.length} tone="ok" />
          </div>
          {current && (
            <div className="mb-2" data-testid="staff-current-task">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#2D5A27] mb-1 inline-flex items-center gap-1">
                <Lightning size={10} weight="fill" />Current job
              </div>
              <Card t={current} primary onSkip={setSkipFor} />
            </div>
          )}
          {todayQueue.length > 0 && (
            <div className="space-y-2">
              {todayQueue.map((t) => <Card key={t.id} t={t} locked onSkip={setSkipFor} />)}
            </div>
          )}
        </section>
      )}

      {/* Iter 185 — Skipped bucket. Tap Reopen to move back to pending. */}
      {view === "list" && skipped.length > 0 && (
        <section data-testid="staff-skipped-tasks">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#7A5A11] font-semibold mb-2 inline-flex items-center gap-1.5">
            <SkipForward size={12} weight="fill" />
            Skipped
            <Pill count={skipped.length} tone="warn" />
            <span className="ml-1 normal-case tracking-normal text-[10px] text-[#7A5A11]/80 font-normal">
              tap "Reopen" to try again
            </span>
          </div>
          <div className="space-y-2">
            {skipped.map((t) => <Card key={t.id} t={t} skipped onReopen={reopen} />)}
          </div>
        </section>
      )}

      {view === "list" && upcoming.length > 0 && (
        <section data-testid="staff-upcoming-tasks">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#1F3A57] font-semibold mb-2 inline-flex items-center gap-1.5">
            <CalendarBlank size={12} weight="fill" />
            Upcoming
            <Pill count={upcoming.length} tone="info" />
            <span className="ml-1 normal-case tracking-normal text-[10px] text-[#1F3A57]/80 font-normal">
              scheduled for later
            </span>
          </div>
          <div className="space-y-2">
            {upcoming.map((t) => <Card key={t.id} t={t} locked upcoming />)}
          </div>
        </section>
      )}

      {view === "list" && done.length > 0 && (
        <section data-testid="staff-done-tasks">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2 inline-flex items-center gap-1.5">
            Completed
            <Pill count={done.length} tone="muted" />
          </div>
          <div className="space-y-2">
            {done.map((t) => <Card key={t.id} t={t} muted />)}
          </div>
        </section>
      )}

      {skipFor && (
        <SkipModal task={skipFor}
                    onClose={() => setSkipFor(null)}
                    onDone={() => { setSkipFor(null); load(); }}/>
      )}
    </div>
  );
}

const Card = ({ t, primary, locked, muted, backlog, upcoming, skipped, onSkip, onReopen }) => {
  const nav = useNavigate();
  const c = t.customer || {};
  const isLocked = locked && t.locked;
  const cardCls = primary
    ? "bg-white border-2 border-[#2D5A27] shadow-sm hover:shadow-md"
    : backlog
      ? "bg-[#F9EDE7] border border-[#F4C7B9] hover:border-[#C25D38]"
      : upcoming
        ? "bg-[#F1F5FA] border border-[#DDE7F1] hover:border-[#5A7FA6]"
        : skipped
          ? "bg-[#FFF7E6] border border-[#F5C518] hover:border-[#D4A017]"
          : isLocked
            ? "bg-[#F7F6F2] border border-dashed border-[#E5E2D9] opacity-80"
            : muted
              ? "bg-white border border-[#E5E2D9] opacity-75"
              : "bg-white border border-[#E5E2D9] hover:border-[#2D5A27]";
  const Inner = (
    <div className={`block rounded-lg p-4 transition-all ${cardCls}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${primary ? "bg-[#2D5A27] text-white" : isLocked ? "bg-[#E5E2D9]" : "bg-[#D4E0D1]"}`}>
          {isLocked ? (
            <Lock size={16} color="#5C5A55" weight="fill" />
          ) : t.status === "completed" ? (
            <CheckCircle size={16} color="#2D5A27" weight="fill" />
          ) : t.status === "in_progress" ? (
            <Truck size={16} color={primary ? "white" : "#2D5A27"} weight="fill" />
          ) : (
            <Package size={16} color={primary ? "white" : "#2D5A27"} weight="fill" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] text-[#8A8780]">
              #{t.sequence ?? "—"}
            </span>
            {t.sr_priority && (
              <span className={`px-2 py-0.5 text-[9px] uppercase tracking-[0.18em] rounded font-semibold ${PRIORITY_STYLE[t.sr_priority] || "bg-[#E5E2D9]"}`}>
                {t.sr_priority}
              </span>
            )}
            {t.sr_service_category_type === "drop" ? (
              <span className="text-[9px] uppercase tracking-[0.16em] text-[#5C5A55] inline-flex items-center gap-0.5">
                <Truck size={9} />Drop
              </span>
            ) : (
              <span className="text-[9px] uppercase tracking-[0.16em] text-[#5C5A55] inline-flex items-center gap-0.5">
                <House size={9} />Home
              </span>
            )}
            {t.bags_count > 0 && (
              <span className="text-[10px] text-[#2D5A27] font-semibold">
                · {t.bags_count} bag{t.bags_count > 1 ? "s" : ""}
              </span>
            )}
            {backlog && t.date && (
              <span className="text-[9px] uppercase tracking-[0.16em] text-[#7A2E18] font-semibold ml-1">
                {t.date}
              </span>
            )}
            {upcoming && t.date && (
              <span className="text-[9px] uppercase tracking-[0.16em] text-[#1F3A57] font-semibold ml-1 inline-flex items-center gap-0.5">
                <CalendarBlank size={9} weight="fill" />{fmtUpcoming(t.date)}
              </span>
            )}
            {isLocked && (
              <span className="ml-auto text-[9px] uppercase tracking-[0.18em] text-[#8A8780] font-semibold inline-flex items-center gap-1">
                <Lock size={9} weight="fill" />Locked
              </span>
            )}
          </div>
          <div className={`mt-1 truncate ${primary ? "font-heading text-lg" : "font-heading text-base"}`}>
            {c.name || "Unknown"}
          </div>
          {c.address && (
            <div className="text-xs text-[#5C5A55] mt-0.5 truncate flex items-center gap-1">
              <MapPin size={10} />
              {c.landmark ? `${c.landmark} · ` : ""}{c.address}
            </div>
          )}
          {c.phone && !isLocked && (
            <div className="text-xs text-[#2D5A27] mt-1 font-mono flex items-center gap-1">
              <Phone size={10} weight="fill" />{c.phone}
            </div>
          )}
          {t.sr_package_name && (
            <div className="text-[10px] text-[#5C5A55] mt-1 inline-flex items-center gap-1">
              <Package size={10} />Package: <strong className="text-[#1A1A1A]">{t.sr_package_name}</strong>
              {t.sr_waste_category && (
                <span className="text-[#8A8780]">· {t.sr_waste_category}</span>
              )}
            </div>
          )}
          {/* Iter 132 — pending bag delivery chip. Iter 193 — tap opens
                the delivery job. Iter 193.1 — must be a <button> (not <a>)
                because the whole Card is already wrapped in a <Link>; a
                nested anchor triggers React validateDOMNesting and forces
                a full page reload. */}
          {t.pending_bag_delivery && (
            <button type="button"
                     onClick={(e) => {
                       e.preventDefault(); e.stopPropagation();
                       nav(`/staff-app/deliveries?open=${t.pending_bag_delivery.id}`);
                     }}
                     data-testid={`task-pending-delivery-${t.id}`}
                     className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-left text-[11px] font-semibold text-[#7A2E18] bg-[#F9EDE7] border border-[#C25D38] rounded-md px-2 py-1 hover:bg-[#F4C7B9] w-full sm:w-auto">
              <span className="inline-flex items-center gap-1.5">
                <Package size={11} weight="fill"/>
                Bag delivery pending — <span className="underline">Deliver first</span>
              </span>
              <span className="text-[10px] opacity-80 font-normal">
                {t.pending_bag_delivery.challan_no && (
                  <span className="font-mono">{t.pending_bag_delivery.challan_no}</span>
                )}
                {t.pending_bag_delivery.bag_count != null && (
                  <> · {t.pending_bag_delivery.bag_count} bag{t.pending_bag_delivery.bag_count !== 1 ? "s" : ""}</>
                )}
              </span>
            </button>
          )}
          {/* Iter 74 — customer's declared primary waste (D-BMW). Helps
              staff pack the right bags before knocking. */}
          {(t.primary_waste_names || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1" data-testid={`task-primary-waste-${t.id}`}>
              {t.primary_waste_names.slice(0, 4).map((n) => (
                <span key={n}
                      className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#F9F0D6] border border-[#F5C518]/50 text-[#7A5A11] font-semibold">
                  {n}
                </span>
              ))}
              {t.primary_waste_names.length > 4 && (
                <span className="text-[9px] text-[#8A8780]">+{t.primary_waste_names.length - 4}</span>
              )}
            </div>
          )}
          {primary && (
            <div className="mt-3 inline-flex items-center gap-1 text-[11px] text-[#2D5A27] font-semibold">
              Start collection <ArrowRight size={11} weight="bold" />
            </div>
          )}
          {/* Iter 185 — Skip reason label on skipped cards. */}
          {skipped && t.skip_reason && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]"
                  data-testid={`task-skip-reason-${t.id}`}>
              <span className="uppercase tracking-[0.14em] text-[#7A5A11] font-semibold">
                Skipped · {t.skip_reason.replace(/_/g, " ")}
              </span>
              {t.skip_notes && (
                <span className="text-[#5C5A55] italic">"{t.skip_notes}"</span>
              )}
            </div>
          )}
          {/* Iter 185 — Action row: Skip on active cards, Reopen on skipped. */}
          {(!skipped && !muted && onSkip && !isLocked
             && (t.status === "pending" || t.status === "in_progress")) && (
            <div className="mt-2">
              <button type="button"
                       onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSkip(t); }}
                       data-testid={`task-skip-${t.id}`}
                       className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#7A5A11] hover:text-[#5A4200] px-2 py-1 rounded-md border border-[#F5C518] bg-[#FFF7E6]">
                <SkipForward size={10} weight="fill"/>Skip
              </button>
            </div>
          )}
          {skipped && onReopen && (
            <div className="mt-2">
              <button type="button"
                       onClick={(e) => { e.preventDefault(); e.stopPropagation(); onReopen(t); }}
                       data-testid={`task-reopen-${t.id}`}
                       className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#2D5A27] hover:text-[#1A3317] px-2 py-1 rounded-md border border-[#B5D5AC] bg-[#F0F7EE]">
                <ArrowClockwise size={10} weight="fill"/>Reopen
              </button>
            </div>
          )}
          {muted && t.completed_at && (
            <div className="text-[10px] text-[#8A8780] mt-1 inline-flex items-center gap-1">
              <Clock size={9} />Completed {new Date(t.completed_at).toLocaleTimeString()}
              {t.draft_invoice_no && (
                <span className="text-[#2D5A27] font-mono ml-1">· {t.draft_invoice_no}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
  if (isLocked) {
    return (
      <div data-testid={`staff-task-${t.id}`} className="cursor-not-allowed">
        {Inner}
      </div>
    );
  }
  return (
    <Link to={`/staff-app/tasks/${t.id}`} data-testid={`staff-task-${t.id}`}>
      {Inner}
    </Link>
  );
};

// Iter 185 — Skip a task with a reason. `later_service` / `wrong_address`
// keep the task in the list under Skipped (reopen-able); other reasons
// cancel the underlying SR.
const SKIP_OPTIONS = [
  { value: "no_customer",      label: "No customer / no response",      cancels: true },
  { value: "gate_closed",      label: "Gate closed / locked",           cancels: true },
  { value: "no_pickup",        label: "Nothing to pick up",             cancels: true },
  { value: "wrong_address",    label: "Wrong address (keep in list)",   cancels: false },
  { value: "later_service",    label: "Come back later (keep in list)", cancels: false },
  { value: "no_service_till",  label: "No service till date (customer travelling)", cancels: true, needsDate: true },
];

const SkipModal = ({ task, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [snooze, setSnooze] = useState("");
  const [busy, setBusy] = useState(false);
  const opt = SKIP_OPTIONS.find(o => o.value === reason);

  const submit = async () => {
    if (!reason) { toast.error("Pick a reason"); return; }
    if (opt?.needsDate && !snooze) { toast.error("Pick the resume date"); return; }
    setBusy(true);
    try {
      await staffApi.post(`/staff-app/tasks/${task.id}/skip`, {
        reason, notes: notes || null, snooze_until: opt?.needsDate ? snooze : null,
      });
      toast.success(opt?.cancels ? "Task cancelled" : "Task skipped — reopen anytime");
      onDone();
    } catch (e) { toast.error(formatStaffErr(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3"
         onClick={onClose} data-testid="staff-skip-modal">
      <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md border border-[#E5E2D9] shadow-lg"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#F0ECE3]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#7A5A11] font-semibold">
              Skip task
            </div>
            <div className="font-semibold text-[15px] text-[#1A1A1A] truncate max-w-[240px]">
              {task.customer?.name || task.sr_request_no || task.id}
            </div>
          </div>
          <button onClick={onClose} data-testid="staff-skip-close"
                   className="p-1.5 rounded hover:bg-[#F7F6F2]">
            <X size={14}/>
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="space-y-1.5">
            {SKIP_OPTIONS.map(o => (
              <label key={o.value}
                      data-testid={`staff-skip-reason-${o.value}`}
                      className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer ${
                        reason === o.value
                          ? "border-[#7A5A11] bg-[#FFF7E6]"
                          : "border-[#E5E2D9] hover:border-[#B5B2AB]"
                      }`}>
                <input type="radio" name="reason" value={o.value}
                        checked={reason === o.value}
                        onChange={() => setReason(o.value)}
                        className="mt-0.5"/>
                <div className="flex-1">
                  <div className="text-[13px] font-medium text-[#1A1A1A]">{o.label}</div>
                  <div className={`text-[10px] uppercase tracking-[0.14em] mt-0.5 ${o.cancels ? "text-[#7A2E18]" : "text-[#2D5A27]"}`}>
                    {o.cancels ? "cancels service" : "keeps in skipped list"}
                  </div>
                </div>
              </label>
            ))}
          </div>
          {opt?.needsDate && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">
                Resume service on
              </label>
              <input type="date" value={snooze} onChange={e => setSnooze(e.target.value)}
                     data-testid="staff-skip-snooze-date"
                     min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                     className="w-full mt-1 h-9 px-2 border border-[#E5E2D9] rounded text-[13px]"/>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">
              Notes (optional)
            </label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
                       data-testid="staff-skip-notes"
                       rows={2}
                       placeholder="e.g., gate padlocked, security said family away"
                       className="w-full mt-1 px-2 py-1.5 border border-[#E5E2D9] rounded text-[13px] resize-none"/>
          </div>
        </div>
        <div className="p-3 border-t border-[#F0ECE3] flex gap-2">
          <button onClick={onClose} data-testid="staff-skip-cancel"
                   className="flex-1 h-10 rounded-md border border-[#E5E2D9] text-[13px] font-semibold text-[#5C5A55] hover:bg-[#F7F6F2]">
            Never mind
          </button>
          <button onClick={submit} disabled={busy || !reason}
                   data-testid="staff-skip-confirm"
                   className="flex-1 h-10 rounded-md bg-[#7A5A11] text-white text-[13px] font-semibold hover:bg-[#5A4200] disabled:opacity-50 inline-flex items-center justify-center gap-1">
            <SkipForward size={12} weight="fill"/>{busy ? "Skipping…" : "Confirm skip"}
          </button>
        </div>
      </div>
    </div>
  );
};

